import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { AccountContext } from '@/features/account-context';
import { CardButton, MixedCardLayout } from '@/components/card-button';
import { Skeleton } from '@/components/skeletons';
import { FALLBACK_IMAGE, selectStore } from '@/components/recent-stores-list';
import { StoreCardDetails } from '@/features/accounts/store-card-details';
import {
  fillWithConnectedStores,
  getRecentStores,
  readCachedNewestAccounts,
  RecentStore,
  refreshNewestAccounts,
  toRecentStore,
} from '@/utils/recent-stores';
import { ConnectedStore } from '@/webapi/use-accounts-api';
import { routes } from '@/webapi/routes';

interface RecentStoreWithStats extends RecentStore {
  stats?: ConnectedStore;
}

function withStats(
  stores: RecentStore[],
  accounts: ConnectedStore[],
): RecentStoreWithStats[] {
  const statsByAlias = new Map(accounts.map((a) => [a.storeAlias, a]));
  return stores.map((store) => ({
    ...store,
    stats: statsByAlias.get(store.alias),
  }));
}

function toRecentStoresWithStats(
  accounts: ConnectedStore[],
): RecentStoreWithStats[] {
  const ordered = fillWithConnectedStores(
    getRecentStores(),
    accounts.map(toRecentStore),
  );
  return withStats(ordered, accounts);
}

function useRecentStoresWithStats() {
  const [accounts, setAccounts] = useState<ConnectedStore[] | null>(
    readCachedNewestAccounts,
  );

  useEffect(() => {
    let cancelled = false;
    refreshNewestAccounts().then((fresh) => {
      if (cancelled) return;
      setAccounts((current) => (fresh.length > 0 ? fresh : current || []));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const stores = useMemo(
    () => (accounts ? toRecentStoresWithStats(accounts) : []),
    [accounts],
  );

  return { stores, isLoading: !accounts };
}

export const RecentStoresVerticalList = () => {
  const ctx = useContext(AccountContext);
  const currentAlias = ctx?.account?.store?.alias;
  const { stores, isLoading } = useRecentStoresWithStats();
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [stores, updateScrollState]);

  if (isLoading) {
    return <StoresListSkeleton />;
  }

  if (stores.length <= 1) {
    return null;
  }

  return (
    <Wrapper>
      <StoresList ref={listRef} onScroll={updateScrollState}>
        {stores.map((store) => (
          <StoreCard
            key={store.alias}
            className={store.alias === currentAlias ? `active` : ``}
            canBeSelected
            height="auto"
            padding="1.2rem"
            borderRadius="1.4rem"
            onClicked={() => selectStore(store.alias)}
          >
            <MixedCardLayout
              primaryColor="black"
              accentColor="#758390"
              horizontalDistribution="6rem 1fr"
              horizontalGap="1.4rem"
              verticalGap="0.6rem"
              content={
                <StoreImage
                  src={routes.prodStoreSnapshotUrl(store.alias)}
                  alt={store.alias}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = FALLBACK_IMAGE;
                  }}
                />
              }
            >
              <StoreCardDetails storeAlias={store.alias} stats={store.stats} />
            </MixedCardLayout>
          </StoreCard>
        ))}
      </StoresList>
      <BottomFade $show={canScrollDown} />
    </Wrapper>
  );
};

const StoreCardSkeleton = () => (
  <SkeletonCard>
    <Skeleton width="6rem" height="8rem" br="0.8rem" />
    <SkeletonLines>
      <Skeleton width="60%" height="1.3rem" />
      <Skeleton width="85%" height="1rem" />
      <Skeleton width="70%" height="1rem" />
      <Skeleton width="50%" height="1rem" />
    </SkeletonLines>
  </SkeletonCard>
);

const StoresListSkeleton = () => (
  <Wrapper>
    <StoresList>
      <StoreCardSkeleton />
      <StoreCardSkeleton />
    </StoresList>
  </Wrapper>
);

const Wrapper = styled.div`
  position: relative;
  margin: 0.8rem -2rem 0;
`;

const StoresList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  max-height: 18.5rem;
  padding: 0.4rem;
  overflow-y: auto;
  overscroll-behavior: contain;

  scrollbar-width: none;
  &&::-webkit-scrollbar {
    display: none;
  }
`;

const StoreCard = styled(CardButton)`
  && span {
    font-size: 1rem !important;
    overflow-wrap: anywhere;
  }

  && .store-name {
    font-size: 1.3rem !important;
  }

  &&.active {
    border-color: #414141;
  }
`;

const SkeletonCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  flex-shrink: 0;

  padding: 1.2rem;
  border: 1px solid #eaecf1;
  border-radius: 1.4rem;
`;

const SkeletonLines = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.8rem;
  flex: 1;
`;

const StoreImage = styled.img`
  width: 6rem;
  height: 8rem;
  object-fit: cover;
  object-position: top;
  border-radius: 0.8rem;
  box-shadow: 1px 1px 10px 1px rgba(0, 0, 0, 0.08);
`;

const BottomFade = styled.div<{ $show: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 5.5rem;

  background: linear-gradient(to bottom, transparent, white 75%);

  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.2s ease-out;
  pointer-events: none;
`;
